import React, { useEffect, useState } from 'react';

import {
    GetPaperPlanMappingsInput,
    GetPaperPlanMappingsOutput,
    PaperPlanMapping
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { isEqual } from 'lodash';
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BreadcrumbWrapper } from 'src/components/Breadcrumb';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { StepTracker } from 'src/components/StepTracker';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { initialAddPaperPlanMappingState } from 'src/data/PaperPlanMappingData';
import { IBreadcrumb } from 'src/types/IBreadcrumb';
import { IPaperPlanMapping, IPaperPlanMappingParams } from 'src/types/IPPRPlanMappings';
import { IStepTrackerStep } from 'src/types/IStepTrackerStep';
import { IToast } from 'src/types/IToaster';
import { convertTimestampToYYYYMMDDInUTCTimezone } from 'src/util/dateTimeUtil';

import { PaperPlanMappingDetails } from './PaperPlanMappingDetails';
import { PaperPlanMappingReview } from './PaperPlanMappingReview';

export const AddPaperPlanMapping = () => {
    const [paperPlanMapping, setPaperPlanMapping] = useState<IPaperPlanMapping>(initialAddPaperPlanMappingState);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
    const { metadata, setToasts, setTopAlertMsg } = React.useContext(AppContext);
    const territories = Object.values(metadata.territoryMapping);
    const history = useHistory();

    const params: IPaperPlanMappingParams = useParams();
    const paperPlanMappingId = parseInt(params.paperPlanMappingId);
    const [isEditMode] = useState<boolean>(!!paperPlanMappingId);

    useEffect(() => {
        if (!isEditMode) return;

        contractConfigurationClient
            .getPaperPlanMappings(GetPaperPlanMappingsInput.clone({ paperPlanMappingId }))
            .subscribe(
                (response: GetPaperPlanMappingsOutput) => {
                    if (response.paperPlanMappings && response.paperPlanMappings.length > 0) {
                        const mappedPaperPlanMapping = mapResponseToPaperPlanMapping(response.paperPlanMappings[0]);
                        setPaperPlanMapping(mappedPaperPlanMapping);
                    } else {
                        const toast: IToast = {
                            id: 'paper-plan-mapping-not-found-toast',
                            timeout: DEFAULT_TOASTER_TIMEOUT,
                            message: 'PAPER plan mapping does not exist',
                            type: 'error'
                        };
                        setToasts([toast]);
                        history.push(ROUTES.PAPER_PLAN_MAPPINGS.INDEX);
                    }
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getPaperPlanMappings unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    }, []);

    const getTerritoryId = (territoryCode: string) => {
        return territories.find((territory) => territory.territoryCode === territoryCode)?.territoryId;
    };

    const mapResponseToPaperPlanMapping = (response: PaperPlanMapping) => {
        return {
            paperPlanMappingId: response.paperPlanMappingId || '',
            paperPlanId: response.paperPlan?.paperPlanId || '',
            paperPlanName: response.paperPlan?.paperPlanName || '',
            basePlanId: response.basePlan?.basePlanId || '',
            basePlanName: response.basePlan?.planName || '',
            basePlanPromoId:
                (response.basePromotions &&
                    response.basePromotions.length > 0 &&
                    response.basePromotions[0].promotionId) ||
                '',
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            territoryId: getTerritoryId(response.territoryCode!),
            territoryIds: [],
            startDate: response.startDate ? convertTimestampToYYYYMMDDInUTCTimezone(response.startDate) : '',
            endDate: response.endDate ? convertTimestampToYYYYMMDDInUTCTimezone(response.endDate) : '',
            description: response.description || ''
        } as IPaperPlanMapping;
    };

    const basePath =
        ROUTES.PAPER_PLAN_MAPPINGS.INDEX +
        (isEditMode ? ROUTES.PAPER_PLAN_MAPPINGS.EDIT + `/${paperPlanMappingId}` : ROUTES.PAPER_PLAN_MAPPINGS.ADD);

    const breadcrumbItems: IBreadcrumb[] = [
        { link: ROUTES.PAPER_PLAN_MAPPINGS.INDEX, title: 'PAPER plan mappings' },
        { link: '', title: isEditMode ? 'Edit PAPER plan mapping' : 'Add PAPER plan mapping' }
    ];

    const steps: IStepTrackerStep[] = [
        {
            stepId: ROUTES.PAPER_PLAN_MAPPINGS.STEP.DETAILS,
            stepTitle: isEditMode ? 'Edit details' : 'Mapping details',
            StepComponent: PaperPlanMappingDetails
        },
        {
            stepId: ROUTES.PAPER_PLAN_MAPPINGS.STEP.REVIEW,
            stepTitle: isEditMode ? 'Review and save' : 'Review and add',
            StepComponent: PaperPlanMappingReview
        }
    ];

    const { pathname } = useLocation();
    const step = `/${pathname.split('/').pop()}`;
    const currentStepIndex = steps.findIndex(({ stepId }) => stepId === step);

    const onNext = () => {
        history.push(`${basePath}${steps[currentStepIndex + 1].stepId}`);
    };

    const onPrevious = () => {
        history.push(`${basePath}${steps[currentStepIndex - 1].stepId}`);
    };

    const onCancel = () => {
        if (!isEqual(paperPlanMapping, initialAddPaperPlanMappingState)) {
            setIsCancelModalOpen(true);
        } else {
            history.push(ROUTES.PAPER_PLAN_MAPPINGS.INDEX);
        }
    };

    return (
        <Column alignmentHorizontal="center" spacingInset="500 500 600">
            <Column width={950} maxWidth="100%" spacing="500">
                <BreadcrumbWrapper breadcrumbItems={breadcrumbItems} />
                <Row alignmentVertical="top" wrap="down" widths={['fit', 'fill']}>
                    <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                        <StepTracker steps={steps} basePath={basePath} currentStepIndex={currentStepIndex} />
                    </Column>
                    <Column spacing="300" maxWidth="550px">
                        <Switch>
                            {steps.map(({ stepId, StepComponent }) => (
                                <Route key={stepId} path={`${basePath}${stepId}`}>
                                    <StepComponent
                                        paperPlanMapping={paperPlanMapping}
                                        setPaperPlanMapping={setPaperPlanMapping}
                                        onNext={onNext}
                                        onCancel={onCancel}
                                        onPrevious={onPrevious}
                                        isEditMode={isEditMode}
                                    />
                                </Route>
                            ))}
                        </Switch>
                    </Column>
                </Row>
            </Column>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                title={isEditMode ? 'Cancel edit mapping' : 'Cancel add mapping'}
                body="Are you sure you want to cancel? Any changes will be lost."
                primaryButtonText={isEditMode ? 'Cancel edit mapping' : 'Cancel add mapping'}
                primaryButtonOnClick={() => {
                    history.push(ROUTES.PAPER_PLAN_MAPPINGS.INDEX);
                }}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
            />
        </Column>
    );
};
